import React, {MutableRefObject, useEffect, useRef, useState} from "react";
import JobDisplay from "../../controllers/JobDisplay";
import {dateFormat} from "../../date";
import ViewJob from "./ViewJob";
import {JobTableFunctions} from "./Jobs";
import {AxiosResponse} from "axios";
import PaginationResponse from "../../controllers/PaginationResponse";
import PaginationRequestSearch from "../../controllers/PaginationRequestSearch";
import CreateJob, {JobStatus} from "./createjob";
import PagedSearchTable, {PagedTableFunctions, TableColumn} from "../../components/PagedSearchTable";


export function useMountedPromise() {
    const mounted = useRef(false);

    function createPromise<T>(promise: Promise<T>): Promise<T> {
        return new Promise<T>((res, rej) => {
            promise.then(resp => {
                if (mounted.current)
                    res(resp);
            }).catch(err => {
                rej(err);
            })
        });
    }
    useEffect(() => {
        mounted.current = true;
        
        return () => {
            mounted.current = false;
        }
    }, []);

    return {
        createPromise,
        mounted
    }
}


const JobTable: React.FC<{ 
    completed: boolean,
    archived: boolean,
    componentRef: MutableRefObject<JobTableFunctions | undefined>,
    call: (request: PaginationRequestSearch) => Promise<AxiosResponse<PaginationResponse<JobDisplay>>>
}> = (props) => {
    const pagedTableRef = useRef<PagedTableFunctions<JobDisplay>>();

    const [editJobId, setEditJobId] = useState<JobStatus>('closed')
    const [viewJobId, setViewJobId] = useState<number>()

    React.useImperativeHandle(props.componentRef, () => ({
        refresh: () => pagedTableRef.current?.refresh()
    }))

    const columns: TableColumn<JobDisplay>[] = [
        {
            header: 'Job',
            key: 'id',
            row: (row) => <>J{row.id}</>
        },
        {
            header: 'Entities',
            key: 'entity',
            row: (row) => row.entity
        },
        {
            header: 'Farms',
            key: 'farms',
            row: (row) => row.farms
        },
        {
            header: 'Samples',
            key: 'samples',
            row: (row) => {
                const parts = row.samples.split(", ").filter(s => s)
                if (parts.length === 0) return ''
                if (parts.length === 1) return parts[0]
                return `${parts[0]} to ${parts[parts.length - 1]}`
            }
        },
        {
            header: 'Progress',
            key: 'progress',
            row: (row) => <>{row.samplesCompleted}/{row.samplesTotal}</>
        },
        {
            header: 'Created',
            key: 'created',
            row: (row) => dateFormat(row.created)
        },
        {
            header: 'Created By',
            key: 'createdBy',
            row: (row) => row.createdBy
        },
        {
            header: 'Received',
            key: 'received',
            row: (row) => dateFormat(row.received)
        },
        {
            header: <div className='text-right'>Actions</div>,
            key: 'actions',
            row: (row) => <div className='flex items-center justify-end text-right'>
                <div className="btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); setViewJobId(row.id) }}>view</div>
                <div className="btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); setEditJobId(row.id == 0 ? 'create' : row.id) }}>edit</div>
            </div>
        },
    ]

    return (
        <div>
            <PagedSearchTable
                componentRef={pagedTableRef}
                call={props.call}
                keyExtractor={job => job.id}
                columns={columns}
            />

            {viewJobId ? <ViewJob id={viewJobId} refresh={() => pagedTableRef.current?.refresh()} setId={setViewJobId} completed={props.completed}/> : null}

            <CreateJob
                jobId={editJobId}
                setModelValue={show => {
                    if (show) return;

                    setEditJobId('closed')
                    pagedTableRef.current?.refresh()
            }} />
        </div>
    )
}

export default JobTable;
